import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { SourceType } from "@shared/websiteSubmissions";

export interface SubmissionRow {
  id: string;
  source_type: SourceType;
  source_id: string;
  submitted_at: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  company_name: string | null;
  headline: string | null;
  status: string;
  assigned_to_id: string | null;
  assigned_to_name: string | null;
  converted_company_id: string | null;
  converted_contact_id: string | null;
  /** Comparable dollar figure for the list's magnitude bar; null where the
   *  type has no size (subscribers) or the upstream field was not numeric. */
  magnitude: number | null;
  /** The one extra triage fact for this type — industry for a valuation,
   *  target industries for a buyer. Null for subscribers. */
  subtitle: string | null;
}

export interface SubmissionDetail extends SubmissionRow {
  payload: Record<string, unknown>;
  internal_notes: string | null;
  reviewed_at: string | null;
  synced_at: string;
  converted_company_name: string | null;
  converted_company_category: string | null;
}

export interface WebsiteStats {
  byType: {
    source_type: SourceType;
    total: number;
    unreviewed: number;
    last_synced_at: string | null;
  }[];
  configured: boolean;
}

export function useWebsiteSubmissions(params: {
  type: SourceType;
  status?: string;
  q?: string;
  page?: number;
}) {
  const search = new URLSearchParams({ type: params.type });
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  if (params.page) search.set("page", String(params.page));

  return useQuery<{ rows: SubmissionRow[]; total: number; page: number; pageSize: number }>({
    queryKey: ["/api/website/submissions", params],
    queryFn: async () => (await apiRequest("GET", `/api/website/submissions?${search}`)).json(),
  });
}

export function useWebsiteStats() {
  return useQuery<WebsiteStats>({
    queryKey: ["/api/website/stats"],
    queryFn: async () => (await apiRequest("GET", "/api/website/stats")).json(),
  });
}

export function useSubmissionDetail(id: string | null) {
  return useQuery<SubmissionDetail>({
    queryKey: ["/api/website/submissions/detail", id],
    queryFn: async () => (await apiRequest("GET", `/api/website/submissions/${id}`)).json(),
    enabled: Boolean(id),
  });
}

/** Invalidate every website query. Used after any write, so counts and the
 *  open detail panel cannot disagree with the list behind it. */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["/api/website/submissions"] });
  qc.invalidateQueries({ queryKey: ["/api/website/submissions/detail"] });
  qc.invalidateQueries({ queryKey: ["/api/website/stats"] });
}

export function useSyncWebsite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/website/sync")).json(),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useTriage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      status?: string;
      assignedToId?: string | null;
      internalNotes?: string | null;
    }) => (await apiRequest("PATCH", `/api/website/submissions/${id}`, body)).json(),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useConvert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiRequest("POST", `/api/website/submissions/${id}/convert`)).json(),
    onSuccess: () => invalidateAll(qc),
  });
}
